import request from './request'
import type { CareOrderListItem } from './orders'

export type CareScheduleAssignTargetType = 'order' | 'followup'

/** POST `/api/admin/care-schedule/assign` 请求体（raw-json） */
export interface CareScheduleAssignRequest {
  target_type: CareScheduleAssignTargetType
  /** 主单为订单主键 `id`；Follow-up 为 follow-up 主键 */
  target_id: number
  technician_id: number
  /** 服务日期 `YYYY-MM-DD` */
  service_date: string
  /** 开始时间 `HH:mm`（24 小时制） */
  time_start: string
  /** 结束时间 `HH:mm`（24 小时制）；与用户所选最后一格结束时刻一致 */
  time_end?: string
}

export interface CareScheduleAssignResponse {
  code: number
  msg: string
  data?: unknown
}

export interface CareScheduleAssignResult {
  response: CareScheduleAssignResponse
  /** assign 成功后后端返回的订单快照（若有） */
  order: CareOrderListItem | null
}

function isCareOrderListItemShape(row: unknown): row is CareOrderListItem {
  if (!row || typeof row !== 'object') return false
  const o = row as Record<string, unknown>
  return typeof o.order_id === 'string' || typeof o.id === 'number'
}

/** 从 assign 响应 `data` 解析订单行（兼容多种字段名） */
export function parseCareScheduleAssignResponse(raw: unknown): CareOrderListItem | null {
  if (raw == null || typeof raw !== 'object') return null
  const r = raw as CareScheduleAssignResponse
  if (r.code !== 200 || r.data == null) return null
  if (isCareOrderListItemShape(r.data)) return r.data

  if (typeof r.data === 'object') {
    const d = r.data as Record<string, unknown>
    for (const key of ['order', 'order_detail', 'detail', 'target', 'row'] as const) {
      const nested = d[key]
      if (isCareOrderListItemShape(nested)) return nested
    }
  }
  return null
}

/** 展示用 `3:00 PM` / 格点 `03:00PM` → 接口 `15:00` */
export function displayClockToTime24(clock: string): string {
  const m = clock.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (!m?.[1] || !m[2] || !m[3]) return gridSlotToTimeStart(clock)
  return gridSlotToTimeStart(`${m[1]}:${m[2]}${m[3]}`)
}

/** 日历格 `10:00AM` → 接口 `10:00` */
export function gridSlotToTimeStart(slot: string): string {
  const m = slot.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (!m?.[1] || !m[2] || !m[3]) return slot.trim()
  let hour = parseInt(m[1], 10)
  const minute = m[2]
  const meridiem = m[3].toUpperCase()
  if (meridiem === 'PM' && hour !== 12) hour += 12
  if (meridiem === 'AM' && hour === 12) hour = 0
  return `${String(hour).padStart(2, '0')}:${minute}`
}

export function formatServiceDateYmd(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * 主单 / Follow-up 排期。
 * Header `token` 由 `request` 拦截器从 localStorage 自动注入。
 */
export async function postCareScheduleAssign(
  body: CareScheduleAssignRequest,
): Promise<CareScheduleAssignResult> {
  const raw = await request.post<CareScheduleAssignResponse>(
    '/api/admin/care-schedule/assign',
    body,
  )
  if (raw?.code === 200) {
    return {
      response: raw,
      order: parseCareScheduleAssignResponse(raw),
    }
  }
  const msg =
    typeof raw?.msg === 'string' && raw.msg.trim()
      ? raw.msg
      : 'Failed to assign schedule'
  throw new Error(msg)
}
